import { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/useAuthStore';
import { ordersApi } from '@/lib/api';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import DashboardPage from '@/pages/customer/DashboardPage';
import { entryFor } from '@/store/profileLoad';

// Brand palette — matches the builder (AlbumFlowPage) so the card reads as the
// same product, not a separate screen.
const C = {
  gold:      '#C9A24A',
  goldDeep:  '#A8842F',
  goldLight: '#D4B86A',
  beige:     '#F5EFE0',
  dark:      '#1F2937',
  muted:     '#6B7280',
  white:     '#FFFFFF',
};
const SERIF = "'Fraunces', 'Cormorant Garamond', Georgia, serif";
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

type DraftRef = { o: string; b: string; t: number };

// The bridge cookie (Domain=.storiz.ma) the COD form + builder write. Readable
// here because app.storiz.ma is a subdomain of storiz.ma.
function readDraftCookie(): DraftRef | null {
  try {
    const row = document.cookie.split('; ').find((r) => r.startsWith('storiz_draft='));
    if (!row) return null;
    const obj = JSON.parse(decodeURIComponent(row.split('=').slice(1).join('=')));
    if (!obj || !obj.o || typeof obj.t !== 'number') return null;
    if (Date.now() - obj.t > DRAFT_MAX_AGE_MS) return null; // older than 24h → ignore
    return obj as DraftRef;
  } catch {
    return null;
  }
}

function clearDraftCookie() {
  try {
    document.cookie = 'storiz_draft=; Domain=.storiz.ma; Path=/; Max-Age=0; SameSite=Lax; Secure';
  } catch { /* ignore */ }
}

const STOREFRONT_URL = 'https://storiz.ma';

// Root route. A returning COD customer (not logged in) who left an order
// in progress sees a resume card instead of the login wall. Everyone else —
// logged-in users, or visitors with no valid in-progress order — gets the
// normal protected dashboard, unchanged. Deep links (/flow/:id) bypass this
// entirely and go straight in.
export default function ResumeLanding() {
  const { user, profile, profileError } = useAuthStore();
  const [phase, setPhase] = useState<'checking' | 'resume' | 'fallback'>('checking');
  const [draft, setDraft] = useState<DraftRef | null>(null);

  useEffect(() => {
    // Never hijack a logged-in session (admins/customers land on the dashboard).
    if (user) { setPhase('fallback'); return; }
    const cookie = readDraftCookie();
    if (!cookie) { setPhase('fallback'); return; }

    let cancelled = false;
    (async () => {
      try {
        const { order } = await ordersApi.get(cookie.o);
        if (cancelled) return;
        // Only offer resume while the album is still being built.
        if (order && order.status === 'pending-album') {
          setDraft(cookie);
          setPhase('resume');
        } else {
          clearDraftCookie();
          setPhase('fallback');
        }
      } catch {
        if (!cancelled) setPhase('fallback');
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Team members (admin/staff) belong in the admin workspace, NOT the customer
  // « Mes commandes » page (2026-07-17). ⚠️ Decided by `entryFor`, AFTER the
  // hooks above (an early return before a hook breaks React's hook order), and
  // only on a role the server gave: a signed-in person whose profile is still
  // loading waits in ProtectedRoute — never the customer door (2026-09-25).
  const entry = entryFor({ user, profile, profileError });
  if (typeof entry === 'object') return <Navigate to={entry.go} replace />;

  if (phase === 'checking') return <CheckingScreen />;
  if (phase === 'resume' && draft) return <ResumeCard draft={draft} />;
  return (
    <ProtectedRoute>
      <DashboardPage />
    </ProtectedRoute>
  );
}

function CheckingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: C.beige }}>
      <div
        className="w-9 h-9 rounded-full border-2 animate-spin"
        style={{ borderColor: `${C.gold}40`, borderTopColor: C.gold }}
        aria-label="Chargement"
      />
    </div>
  );
}

function ResumeCard({ draft }: { draft: DraftRef }) {
  const navigate = useNavigate();
  const bundle = (draft.b || '').trim();

  return (
    <div
      className="min-h-screen flex items-center justify-center px-5 py-10"
      style={{
        // Soft gold glow over cream — warm, premium, not flat.
        background: `radial-gradient(120% 80% at 50% 0%, ${C.gold}1F 0%, ${C.beige} 55%)`,
      }}
    >
      <style>{`
        @keyframes storizResumeRise {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .storiz-resume-card { animation: none !important; }
        }
      `}</style>

      <div
        className="storiz-resume-card w-full max-w-sm rounded-[28px] text-center px-7 pt-9 pb-7"
        style={{
          background: C.white,
          boxShadow: '0 24px 60px rgba(31,41,55,0.16)',
          border: `1px solid ${C.gold}22`,
          animation: 'storizResumeRise 520ms cubic-bezier(0.22, 1, 0.36, 1) both',
        }}
      >
        {/* Signature — a tilted album cover with a gold spine + a thin progress
            sliver, evoking a book left open mid-build. The one bold element. */}
        <div className="flex justify-center mb-6" aria-hidden="true">
          <AlbumMark />
        </div>

        <p
          className="text-[11px] font-semibold mb-2"
          style={{ color: C.goldDeep, letterSpacing: '0.22em' }}
        >
          VOTRE ALBUM
        </p>
        <h1
          className="text-[28px] leading-tight mb-3"
          style={{ fontFamily: SERIF, color: C.dark, fontWeight: 600 }}
        >
          Votre album vous attend
        </h1>
        <p className="text-sm leading-relaxed mb-1" style={{ color: C.muted }}>
          Vos photos et votre mise en page sont sauvegardées.
        </p>
        <p className="text-sm leading-relaxed" style={{ color: C.muted }}>
          Reprenez exactement là où vous vous êtes arrêté.
        </p>

        {bundle && (
          <div
            className="inline-flex items-center gap-1.5 mt-5 px-3 py-1 rounded-full"
            style={{ background: `${C.gold}14`, color: C.goldDeep }}
          >
            <span className="text-[11px] font-semibold tracking-wide">Formule {bundle}</span>
          </div>
        )}

        <button
          onClick={() => navigate(`/flow/${draft.o}`)}
          className="w-full mt-7 py-4 rounded-2xl text-base font-bold tracking-wide transition-transform active:scale-[0.99]"
          style={{
            background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
            color: C.white,
            boxShadow: `0 8px 22px ${C.gold}55`,
          }}
        >
          Reprendre mon album →
        </button>

        <button
          onClick={() => { window.location.href = STOREFRONT_URL; }}
          className="w-full mt-3 py-2.5 text-sm font-medium rounded-xl transition-colors hover:bg-gray-50"
          style={{ color: C.muted }}
        >
          Commencer une nouvelle commande
        </button>
      </div>
    </div>
  );
}

// Album-cover signature mark. Cream cover, gold spine + edge, a thin gold
// "progress" sliver near the foot to read as work-in-progress.
function AlbumMark() {
  return (
    <svg width="84" height="84" viewBox="0 0 84 84" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* soft halo */}
      <circle cx="42" cy="42" r="40" fill={C.gold} opacity="0.08" />
      {/* book body, slightly tilted */}
      <g transform="rotate(-7 42 42)">
        <rect x="24" y="20" width="36" height="46" rx="4" fill={C.white} stroke={C.gold} strokeWidth="2.2" />
        {/* spine */}
        <rect x="24" y="20" width="6.5" height="46" rx="3" fill={C.gold} />
        {/* cover title bars */}
        <rect x="36" y="33" width="18" height="3.2" rx="1.6" fill={C.gold} opacity="0.55" />
        <rect x="36" y="40" width="12" height="3.2" rx="1.6" fill={C.gold} opacity="0.35" />
        {/* progress sliver near the foot */}
        <rect x="36" y="56" width="18" height="3.6" rx="1.8" fill={`${C.gold}33`} />
        <rect x="36" y="56" width="11" height="3.6" rx="1.8" fill={C.goldDeep} />
      </g>
    </svg>
  );
}
